import { t } from '@transifex/native';

import * as flashes from 'pkg/actions/flashes';

import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import { create as modelsCreate } from 'pkg/api/models/actions';
import type { VideoPlaylistSequencePermissions } from 'pkg/api/models/video_playlist';
import * as sdk from 'pkg/core/sdk';

import { Event, trackEvent, trackedGroup } from 'components/analytics';

interface PlaylistResponse {
	id: number;
	accountId: number;
	groupId?: number;
	title: string;
	isPrivate: boolean;
	sequences?: JSONObject;
	createdAt?: number;
	updatedAt?: number;
}

export interface PlaylistCreatePayload {
	groupId?: number;
	title: string;
	sequencePermissions: VideoPlaylistSequencePermissions[];
}

export const create = async (
	organizationId: number,
	payload: PlaylistCreatePayload
): Promise<models.videoPlaylist.VideoPlaylist> => {
	const [request, response] = await modelsCreate<
		PlaylistCreatePayload,
		models.videoPlaylist.VideoPlaylist
	>(endpoints.VideoPlaylists.Create(organizationId), payload);

	if (!request.ok) {
		flashes.show({ title: t(`Could not create playlist`) }, request.status);
		return;
	} else {
		flashes.show({ title: t(`Created new playlist`) }, request.status);
	}

	trackEvent(Event.VideoPlaylistCreated, trackedGroup(payload.groupId));

	return response;
};

export interface PlaylistUpdatePayload {
	groupId?: number;
	title?: string;
	isPrivate?: boolean;
	sequencePermissions?: VideoPlaylistSequencePermissions[];
}

export const update = async (
	organizationId: number,
	playlistId: number,
	payload: PlaylistUpdatePayload
): Promise<models.videoPlaylist.VideoPlaylist> => {
	const request = await sdk.patch(
		endpoints.VideoPlaylists.Update(organizationId, playlistId),
		{},
		payload
	);

	if (!request.ok) {
		flashes.show({ title: t(`Could not update playlist`) }, request.status);
		return;
	}

	const response: models.videoPlaylist.VideoPlaylist = await request.json();

	return response;
};

export const addSequences = async (
	organizationId: number,
	playlistId: number,
	...sequenceIds: number[]
): Promise<PlaylistResponse> => {
	const request = await sdk.post(
		endpoints.VideoPlaylists.AddSequences(organizationId, playlistId),
		{},
		sequenceIds
	);

	const response: PlaylistResponse = await request.json();

	if (!request.ok) {
		flashes.show(
			{ title: t(`Could not add clips to playlist`) },
			request.status
		);
		return;
	}

	return response;
};
